/**
 * CrossTenantSettingsPanel Component
 *
 * Shared panel component for cross-tenant access settings with Inbound/Outbound tabs.
 * Used in both Default Settings card and Organization modal.
 *
 * - Inbound access tab: B2B inbound toggle + Trust settings (3 toggles)
 * - Outbound access tab: B2B outbound toggle only
 *
 * Two modes:
 * - "default": Values are always non-null (used in Default Settings card)
 * - "organization": Values can be null = inherited from default (used in Organization modal)
 */

import { useState } from 'react';
import {
  TabList,
  Tab,
  Switch,
  Checkbox,
  Text,
  Subtitle2,
  tokens,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import type { InboundTrust, B2BAccessType } from '../../../types/crossTenantAccess';

const useStyles = makeStyles({
  settingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding(tokens.spacingVerticalS, 0),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  settingLabel: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  settingDescription: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  inheritRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalS,
  },
});

type TabValue = 'inbound' | 'outbound';

interface CrossTenantSettingsPanelProps {
  /**
   * Mode determines how null values are handled:
   * - "default": Values are always non-null
   * - "organization": Values can be null (inherited from default)
   */
  mode: 'default' | 'organization';

  /**
   * B2B collaboration settings.
   * In organization mode, null values mean "inherit from default".
   */
  b2bCollaboration: {
    inbound: B2BAccessType | null;
    outbound: B2BAccessType | null;
  };

  /**
   * Inbound trust settings.
   * In organization mode, null means "inherit all trust settings from default".
   */
  inboundTrust: InboundTrust | null;

  /**
   * Called when any setting changes.
   */
  onChange: (settings: {
    b2bCollaboration: { inbound: B2BAccessType | null; outbound: B2BAccessType | null };
    inboundTrust: InboundTrust | null;
  }) => void;

  /**
   * Disable all controls (during save).
   */
  disabled?: boolean;
}

/**
 * Default trust settings when switching from inherited to custom.
 */
const defaultTrustSettings: InboundTrust = {
  isMfaAccepted: false,
  isCompliantDeviceAccepted: false,
  isHybridAzureADJoinedDeviceAccepted: false,
};

export function CrossTenantSettingsPanel({
  mode,
  b2bCollaboration,
  inboundTrust,
  onChange,
  disabled = false,
}: CrossTenantSettingsPanelProps) {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState<TabValue>('inbound');

  const isOrganizationMode = mode === 'organization';

  // Determine if B2B inbound is inherited (null in organization mode)
  const isB2BInboundInherited = isOrganizationMode && b2bCollaboration.inbound === null;
  const isB2BOutboundInherited = isOrganizationMode && b2bCollaboration.outbound === null;
  const isTrustInherited = isOrganizationMode && inboundTrust === null;

  // Get effective values (use defaults for inherited values in display)
  const effectiveB2BInbound = b2bCollaboration.inbound ?? 'allow';
  const effectiveB2BOutbound = b2bCollaboration.outbound ?? 'allow';
  const effectiveTrust = inboundTrust ?? defaultTrustSettings;

  const handleB2BInboundChange = (checked: boolean) => {
    onChange({
      b2bCollaboration: {
        ...b2bCollaboration,
        inbound: checked ? 'allow' : 'block',
      },
      inboundTrust,
    });
  };

  const handleB2BOutboundChange = (checked: boolean) => {
    onChange({
      b2bCollaboration: {
        ...b2bCollaboration,
        outbound: checked ? 'allow' : 'block',
      },
      inboundTrust,
    });
  };

  const handleTrustChange = (field: keyof InboundTrust, checked: boolean) => {
    const newTrust = inboundTrust ?? defaultTrustSettings;
    onChange({
      b2bCollaboration,
      inboundTrust: {
        ...newTrust,
        [field]: checked,
      },
    });
  };

  const handleB2BInboundInheritChange = (inherit: boolean) => {
    onChange({
      b2bCollaboration: {
        ...b2bCollaboration,
        inbound: inherit ? null : 'allow',
      },
      inboundTrust,
    });
  };

  const handleB2BOutboundInheritChange = (inherit: boolean) => {
    onChange({
      b2bCollaboration: {
        ...b2bCollaboration,
        outbound: inherit ? null : 'allow',
      },
      inboundTrust,
    });
  };

  const handleTrustInheritChange = (inherit: boolean) => {
    onChange({
      b2bCollaboration,
      inboundTrust: inherit ? null : defaultTrustSettings,
    });
  };

  return (
    <div>
      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) => setSelectedTab(data.value as TabValue)}
        style={{ marginBottom: tokens.spacingVerticalL }}
      >
        <Tab value="inbound">Inbound access</Tab>
        <Tab value="outbound">Outbound access</Tab>
      </TabList>

      {/* Inbound Access Tab Content */}
      {selectedTab === 'inbound' && (
        <div>
          {/* B2B Inbound Section */}
          <Subtitle2>B2B Collaboration</Subtitle2>
          <div style={{ paddingLeft: tokens.spacingHorizontalL }}>
            {isOrganizationMode && (
              <div className={styles.inheritRow}>
                <Checkbox
                  checked={isB2BInboundInherited}
                  onChange={(_, data) => handleB2BInboundInheritChange(data.checked === true)}
                  label="Inherit from default"
                  disabled={disabled}
                />
              </div>
            )}
            <div className={styles.settingRow}>
              <div className={styles.settingLabel}>
                <Text weight="semibold">Allow inbound B2B collaboration</Text>
                <Text className={styles.settingDescription}>
                  Allow users from external organizations to access your resources
                </Text>
              </div>
              <Switch
                checked={effectiveB2BInbound === 'allow'}
                onChange={(_, data) => handleB2BInboundChange(data.checked)}
                disabled={disabled || isB2BInboundInherited}
              />
            </div>
          </div>

          {/* Trust Settings Section */}
          <div style={{ marginTop: '32px' }}>
            <Subtitle2>Trust Settings</Subtitle2>
            <div style={{ paddingLeft: tokens.spacingHorizontalL }}>
              {isOrganizationMode && (
                <div className={styles.inheritRow}>
                  <Checkbox
                    checked={isTrustInherited}
                    onChange={(_, data) => handleTrustInheritChange(data.checked === true)}
                    label="Inherit from default"
                    disabled={disabled}
                  />
                </div>
              )}
              <div className={styles.settingRow}>
                <div className={styles.settingLabel}>
                  <Text weight="semibold">Trust MFA from external tenants</Text>
                  <Text className={styles.settingDescription}>
                    Accept MFA claims from external organizations
                  </Text>
                </div>
                <Switch
                  checked={effectiveTrust.isMfaAccepted}
                  onChange={(_, data) => handleTrustChange('isMfaAccepted', data.checked)}
                  disabled={disabled || isTrustInherited}
                />
              </div>

              <div className={styles.settingRow}>
                <div className={styles.settingLabel}>
                  <Text weight="semibold">Trust compliant devices</Text>
                  <Text className={styles.settingDescription}>
                    Accept compliant device claims from external organizations
                  </Text>
                </div>
                <Switch
                  checked={effectiveTrust.isCompliantDeviceAccepted}
                  onChange={(_, data) => handleTrustChange('isCompliantDeviceAccepted', data.checked)}
                  disabled={disabled || isTrustInherited}
                />
              </div>

              <div className={styles.settingRow}>
                <div className={styles.settingLabel}>
                  <Text weight="semibold">Trust hybrid Azure AD joined devices</Text>
                  <Text className={styles.settingDescription}>
                    Accept hybrid Azure AD joined device claims from external organizations
                  </Text>
                </div>
                <Switch
                  checked={effectiveTrust.isHybridAzureADJoinedDeviceAccepted}
                  onChange={(_, data) =>
                    handleTrustChange('isHybridAzureADJoinedDeviceAccepted', data.checked)
                  }
                  disabled={disabled || isTrustInherited}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Outbound Access Tab Content */}
      {selectedTab === 'outbound' && (
        <div>
          <Subtitle2>B2B Collaboration</Subtitle2>
          <div style={{ paddingLeft: tokens.spacingHorizontalL }}>
            {isOrganizationMode && (
              <div className={styles.inheritRow}>
                <Checkbox
                  checked={isB2BOutboundInherited}
                  onChange={(_, data) => handleB2BOutboundInheritChange(data.checked === true)}
                  label="Inherit from default"
                  disabled={disabled}
                />
              </div>
            )}
            <div className={styles.settingRow}>
              <div className={styles.settingLabel}>
                <Text weight="semibold">Allow outbound B2B collaboration</Text>
                <Text className={styles.settingDescription}>
                  Allow your users to access resources in external organizations
                </Text>
              </div>
              <Switch
                checked={effectiveB2BOutbound === 'allow'}
                onChange={(_, data) => handleB2BOutboundChange(data.checked)}
                disabled={disabled || isB2BOutboundInherited}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
